import { PUZZLE_LENGTH } from '@wt/app/game/_components/PuzzleLoader';
import { clearLoadedCustomPuzzle } from '@wt/app/game/_utils/getPuzzle';
import { isYearInValidRange } from '@wt/shared/components/yearslider/utils';
import useAnimatedRouter from '@wt/shared/hooks/useAnimatedRouter';
import {
  createGame,
  deleteCommunityGame,
  editGame,
} from '@wt/utilities/database';
import {
  deleteImages,
  getImages,
  putImage,
} from '@wt/utilities/indexedDBHelper';
import { isEmpty, isNil } from 'lodash';
import { useRouter } from 'next/navigation';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { toast } from 'react-toastify';

import {
  GameFormContextType,
  GameRound,
  GameToEdit,
  initialGameFormContextValue,
  initialNewGameRound,
} from './types';
import { checkForChanges, usePersistedState } from './utils';
import { getSiteMode } from '@wt/utilities/siteMode';

const GameFormContext = createContext<GameFormContextType>(
  initialGameFormContextValue
);

export const useGameFormProvider = () => useContext(GameFormContext);

export const GameFormProvider = ({ children }: { children: ReactNode }) => {
  const { animatedRoute } = useAnimatedRouter();
  const router = useRouter();

  const persistToLocalStorage = () => {
    const save = {
      name,
      publicGame,
      acceptedCopyright,
      acceptedCommunityGuidelines,
      rounds,
    };
    const key =
      'community-game-saved-form' + (isEdit ? '-' + gameToEdit?.slug : '');
    localStorage.setItem(key, JSON.stringify(save));
  };

  const [name, setNameInternal, setName] = usePersistedState<string>(
    '',
    persistToLocalStorage
  );
  const [publicGame, setPublicGameInternal, setPublicGame] =
    usePersistedState<boolean>(true, persistToLocalStorage);
  const [
    acceptedCopyright,
    setAcceptedCopyrightInternal,
    setAcceptedCopyright,
  ] = usePersistedState<boolean>(false, persistToLocalStorage);
  const [
    acceptedCommunityGuidelines,
    setAcceptedCommunityGuidelinesInternal,
    setAcceptedCommunityGuidelines,
  ] = usePersistedState<boolean>(false, persistToLocalStorage);
  const [rounds, setRoundsInternal, setRounds] = usePersistedState<GameRound[]>(
    Array.from({ length: PUZZLE_LENGTH }).map((v, i) => {
      return i === 0
        ? { ...initialNewGameRound, isOpen: true }
        : initialNewGameRound;
    }),
    persistToLocalStorage
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [gameToEdit, setGameToEditInternal] = useState<GameToEdit | null>(null);
  const [showGuidelinesModal, setShowGuidelinesModal] =
    useState<boolean>(false);
  const [firstTimeViewingGuidelinesModal, setFirstTimeViewingGuidelinesModal] =
    useState<boolean>(false);

  const loadSavedForm = async (slug: string | null) => {
    // If we are editing, we must not load here!
    const key = 'community-game-saved-form' + (slug ? '-' + slug : '');
    const savedFormRaw = localStorage.getItem(key);
    if (!savedFormRaw) {
      return;
    }

    const savedForm = JSON.parse(savedFormRaw);

    if (savedForm.name) setNameInternal(savedForm.name);
    if (!isNil(savedForm.publicGame))
      setPublicGameInternal(savedForm.publicGame);
    if (!isNil(savedForm.acceptedCopyright))
      setAcceptedCopyrightInternal(savedForm.acceptedCopyright);
    if (!isNil(savedForm.acceptedCommunityGuidelines))
      setAcceptedCommunityGuidelinesInternal(
        savedForm.acceptedCommunityGuidelines
      );

    // Load from localStorage, and inject images from indexeddb
    const rounds: GameRound[] = savedForm.rounds;
    for (const r of rounds) {
      r.loadingMetadata = false;
    }

    const images = await getImages(slug ?? 'new');
    for (const i of images) {
      if (!isNil(i) && !isNil(i.id)) {
        const blob = new Blob([i.data], { type: i.type });
        rounds[i.id].image = URL.createObjectURL(blob);
      }
    }
    setRoundsInternal(rounds);
  };

  const setGameToEdit = useCallback(
    (game: GameToEdit) => {
      setGameToEditInternal(game);
      setNameInternal(game.name);
      setPublicGameInternal(game.publicGame);
      setRoundsInternal([...game.rounds]);
      setAcceptedCopyrightInternal(true);
      setAcceptedCommunityGuidelinesInternal(true);

      // Now overload with data from storage
      loadSavedForm(game.slug);
    },
    [
      setGameToEditInternal,
      setNameInternal,
      setPublicGameInternal,
      setRoundsInternal,
    ]
  );

  useEffect(() => {
    const storedSeenGuidelines = localStorage.getItem('seen-guidelines');
    let shownGuidelines = false;
    try {
      shownGuidelines =
        storedSeenGuidelines != null ? JSON.parse(storedSeenGuidelines) : false;
    } catch (e) {
      shownGuidelines = false;
    }
    if (shownGuidelines) return;

    setShowGuidelinesModal(true);
    setFirstTimeViewingGuidelinesModal(true);
    localStorage.setItem('seen-guidelines', JSON.stringify(true));
  }, []);

  const closeGuidelinesModal = () => {
    setShowGuidelinesModal(false);
    if (firstTimeViewingGuidelinesModal) {
      setFirstTimeViewingGuidelinesModal(false);
      setAcceptedCommunityGuidelines(true);
    }
  };

  const isEdit: boolean = useMemo(() => {
    return !isNil(gameToEdit);
  }, [gameToEdit]);

  useEffect(() => {
    loadSavedForm(null);
  }, []);

  const formDataValid: boolean = useMemo(() => {
    return (
      !isEmpty(name) &&
      acceptedCopyright &&
      acceptedCommunityGuidelines &&
      isEmpty(rounds.filter((r) => !r.valid))
    );
  }, [name, acceptedCopyright, acceptedCommunityGuidelines, rounds]);

  const imagesUploaded: number = useMemo(() => {
    return rounds.filter((r) => !isNil(r.image)).length;
  }, [rounds]);

  const hasChanges: boolean = useMemo(() => {
    if (!isEdit || isNil(gameToEdit)) return true;
    return checkForChanges(gameToEdit, name, publicGame, rounds);
  }, [name, publicGame, rounds, isEdit, gameToEdit]);

  const hasDraft: boolean = useMemo(() => {
    if (isEdit) return true;
    return (
      checkForChanges(
        {
          slug: '',
          name: '',
          publicGame: true,
          rounds: Array.from(
            { length: PUZZLE_LENGTH },
            () => initialNewGameRound
          ),
        },
        name,
        publicGame,
        rounds
      ) ||
      acceptedCopyright ||
      acceptedCommunityGuidelines
    );
  }, [
    isEdit,
    name,
    publicGame,
    acceptedCopyright,
    acceptedCommunityGuidelines,
    rounds,
  ]);

  const saveGame = async () => {
    try {
      setLoading(true);
      isEdit ? await saveEditedGame() : await saveNewGame();
    } catch (e) {
      toast.error('Something went wrong. Please try again.');
      setLoading(false);
      return;
    }
  };

  const saveNewGame = async () => {
    const slug = await createGame(name, publicGame, rounds);

    if (isNil(slug)) {
      toast.error('Something went wrong. Please try again.');
      setLoading(false);
      return;
    }

    // Clear indexeddb
    localStorage.removeItem('community-game-saved-form');
    await deleteImages('new');

    animatedRoute(
      `/account/games/new/share?name=${name}&slug=${slug}&public=${publicGame}`
    );
    return;
  };

  const saveEditedGame = async () => {
    if (isNil(gameToEdit)) return;

    const gameEdited = await editGame(
      gameToEdit.slug,
      name,
      publicGame,
      rounds
    );

    if (!gameEdited) {
      toast.error('Something went wrong. Please try again.');
      setLoading(false);
      return;
    }

    // Clear indexeddb
    localStorage.removeItem('community-game-saved-form-' + gameToEdit.slug);
    await deleteImages(gameToEdit.slug);

    clearLoadedCustomPuzzle(gameToEdit.slug);
    animatedRoute(
      `/account/games/edit/${gameToEdit.slug}/share?name=${name}&public=${publicGame}`
    );
    return;
  };

  const deleteGame = async () => {
    if (isNil(gameToEdit)) return;

    try {
      setShowConfirmationModal(false);
      setDeleting(true);
      const deleted = await deleteCommunityGame(gameToEdit.slug);

      if (!deleted) {
        toast.error('Something went wrong. Please try again.');
        setDeleting(false);
        return;
      }

      clearLoadedCustomPuzzle(gameToEdit.slug);
      toast.success('Game deleted!');
      setTimeout(() => {
        router.back();
      }, 200);
    } catch (e) {
      console.log(e);
      toast.error('Something went wrong. Please try again.');
      setDeleting(false);
      return;
    }
  };

  const deleteDraft = async () => {
    try {
      setDeleting(true);

      // Clear indexeddb and state
      setNameInternal('');
      setPublicGameInternal(true);
      setRoundsInternal(
        Array.from({ length: PUZZLE_LENGTH }).map((v, i) => {
          return i === 0
            ? { ...initialNewGameRound, isOpen: true }
            : initialNewGameRound;
        })
      );
      setAcceptedCopyrightInternal(false);
      setAcceptedCommunityGuidelinesInternal(false);
      localStorage.removeItem('community-game-saved-form');
      await deleteImages('new');

      setDeleting(false);
      return;
    } catch (e) {
      toast.error('Something went wrong. Please try again.');
      setDeleting(false);
      return;
    }
  };

  const setRoundData = async (
    newRoundData: Partial<GameRound>,
    index: number
  ) => {
    const updatedRound: GameRound = {
      ...rounds[index],
      ...newRoundData,
    };
    updatedRound.valid =
      !isNil(updatedRound.image) &&
      !isNil(updatedRound.location) &&
      (getSiteMode() == 'wheretaken' ||
        isYearInValidRange(updatedRound.year)) &&
      !isEmpty(updatedRound.description);

    const newRounds = [...rounds];
    newRounds.splice(index, 1, updatedRound);
    setRounds(newRounds);

    // Save images to indexeddb (only re-do this if images have changed?)
    if (newRoundData.image) {
      if (newRoundData.image instanceof File) {
        const data = new Blob([newRoundData.image], {
          type: newRoundData.image.type,
        });
        await putImage(gameToEdit?.slug ?? 'new', index, data);
      }
    }
  };

  return (
    <GameFormContext.Provider
      value={{
        name,
        setName,
        publicGame,
        setPublicGame,
        acceptedCopyright,
        setAcceptedCopyright,
        acceptedCommunityGuidelines,
        setAcceptedCommunityGuidelines,
        rounds,
        setRounds,
        setRoundData,
        loading,
        formDataValid,
        imagesUploaded,
        saveGame,
        isEdit,
        setGameToEdit,
        hasChanges,
        showConfirmationModal,
        setShowConfirmationModal,
        hasDraft,
        deleteDraft,
        deleteGame,
        deleting,
        showGuidelinesModal,
        setShowGuidelinesModal,
        closeGuidelinesModal,
        firstTimeViewingGuidelinesModal,
      }}
    >
      {children}
    </GameFormContext.Provider>
  );
};

export default GameFormContext;
